import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Title,
  Text,
  Button,
  PasswordInput,
  Group,
  ThemeIcon,
  Paper,
  Divider,
  Alert,
  Loader,
  Center,
  Badge,
  Stepper,
} from "@mantine/core";
import {
  IconLock,
  IconFingerprint,
  IconShieldLock,
  IconAlertCircle,
  IconKey,
  IconCheck,
} from "@tabler/icons-react";
import {
  getVaultStatus,
  getVaultAuthMethod,
  isWebAuthnAvailable,
  initializeWithWebAuthn,
  initializeWithPassword,
  unlockWithWebAuthn,
  unlockWithPassword,
  hasLegacyKeys,
  type VaultStatus,
  type VaultAuthMethod,
} from "../lib/key-vault";

interface VaultUnlockModalProps {
  opened: boolean;
  onUnlocked: () => void;
  onCancel: () => void;
  /** The action the user was trying to do, for contextual messaging */
  intent?: "create" | "sign" | "keys";
}

const INTENT_MESSAGES: Record<string, { title: string; description: string }> =
  {
    create: {
      title: "Before you create a document",
      description:
        "To prepare a document for signing, we need access to your signing keys. These are stored in an encrypted vault on your device.",
    },
    sign: {
      title: "Before you sign",
      description:
        "To sign a document, we need access to your signing keys to create a cryptographic signature. Your keys are stored in an encrypted vault on your device.",
    },
    keys: {
      title: "Key Management",
      description:
        "Your PGP keys are stored in an encrypted vault on your device. Unlock to view, create, or manage your keys.",
    },
  };

export function VaultUnlockModal({
  opened,
  onUnlocked,
  onCancel,
  intent,
}: VaultUnlockModalProps) {
  const [status, setStatus] = useState<VaultStatus | "loading">("loading");
  const [authMethod, setAuthMethod] = useState<VaultAuthMethod | null>(null);
  const [webAuthnSupported, setWebAuthnSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setupStep, setSetupStep] = useState(0); // 0 = intro, 1 = choose method, 2 = password form
  const [hasMigration, setHasMigration] = useState(false);

  // Check vault status when modal opens
  useEffect(() => {
    if (!opened) return;
    async function init() {
      const [vaultStatus, webauthn] = await Promise.all([
        getVaultStatus(),
        isWebAuthnAvailable(),
      ]);
      const method = await getVaultAuthMethod();
      setStatus(vaultStatus);
      setAuthMethod(method);
      setWebAuthnSupported(webauthn);
      setHasMigration(hasLegacyKeys());
      setError(null);
      setPassword("");
      setConfirmPassword("");
      setSetupStep(0);
    }
    init();
  }, [opened]);

  // ── Setup handlers ───────────────────────────────────────────────────

  const handleSetupWebAuthn = async () => {
    setBusy(true);
    setError(null);
    try {
      await initializeWithWebAuthn();
      onUnlocked();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("PRF extension not supported")) {
        setError(
          "Your device doesn't support passwordless vault encryption. Let's use a master password instead.",
        );
        setSetupStep(2);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSetupPassword = async () => {
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await initializeWithPassword(password);
      onUnlocked();
      setPassword("");
      setConfirmPassword("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // ── Unlock handlers ──────────────────────────────────────────────────

  const handleUnlockWebAuthn = async () => {
    setBusy(true);
    setError(null);
    try {
      await unlockWithWebAuthn();
      onUnlocked();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockPassword = async () => {
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      await unlockWithPassword(password);
      onUnlocked();
      setPassword("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // ── Contextual messaging ─────────────────────────────────────────────

  const intentMsg = INTENT_MESSAGES[intent || "keys"] || INTENT_MESSAGES.keys;
  const isSetup = status === "uninitialized";

  // Loading
  if (status === "loading" && opened) {
    return (
      <Modal opened centered onClose={onCancel} size="md" title="Key Vault">
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      centered
      size="md"
      padding="xl"
      radius="lg"
      withCloseButton
      title={
        <Group gap="sm">
          <ThemeIcon size={28} radius="md" variant="light" color="blue">
            <IconShieldLock size={16} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            {isSetup ? "Set Up Your Key Vault" : "Unlock Key Vault"}
          </Text>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* Contextual explanation */}
        <Alert
          variant="light"
          color="blue"
          radius="md"
          styles={{ message: { lineHeight: 1.5 } }}
        >
          <Text size="sm" fw={600} mb={4}>
            {intentMsg.title}
          </Text>
          <Text size="sm" c="dimmed">
            {intentMsg.description}
          </Text>
        </Alert>

        {error && (
          <Alert
            variant="light"
            color="red"
            icon={<IconAlertCircle size={16} />}
            radius="md"
          >
            {error}
          </Alert>
        )}

        {/* ── FIRST-TIME SETUP ──────────────────────────────────── */}
        {isSetup && setupStep === 0 && (
          <Stack gap="md">
            {/* Friendly explainer stepper */}
            <Stepper
              active={0}
              size="xs"
              color="blue"
              styles={{ stepLabel: { fontSize: 12 } }}
            >
              <Stepper.Step label="Create vault" />
              <Stepper.Step label="Add keys" />
              <Stepper.Step label="Start signing" />
            </Stepper>

            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                <strong>One-time setup:</strong> We'll create an encrypted vault
                on this device to store your signing keys. No account needed,
                everything stays in your browser, protected by AES-256
                encryption.
              </Text>
              {hasMigration && (
                <Text size="sm" c="blue">
                  ✓ We found existing keys on this device, they'll be imported
                  automatically.
                </Text>
              )}
            </Stack>

            <Button size="md" fullWidth onClick={() => setSetupStep(1)}>
              Set Up Vault — takes 10 seconds
            </Button>
          </Stack>
        )}

        {/* ── CHOOSE AUTH METHOD ─────────────────────────────────── */}
        {isSetup && setupStep === 1 && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Choose how you'd like to unlock your vault each session:
            </Text>

            {webAuthnSupported && (
              <>
                <Paper
                  withBorder
                  p="md"
                  radius="md"
                  style={{ cursor: busy ? "wait" : "pointer" }}
                  onClick={busy ? undefined : handleSetupWebAuthn}
                >
                  <Group wrap="nowrap">
                    <ThemeIcon
                      size={44}
                      radius="md"
                      variant="light"
                      color="teal"
                    >
                      <IconFingerprint size={22} />
                    </ThemeIcon>
                    <div>
                      <Group gap="xs">
                        <Text fw={600} size="sm">
                          Biometric / Passkey
                        </Text>
                        <Badge size="xs" color="teal" variant="light">
                          Recommended
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Touch ID, Face ID, or Windows Hello. No password needed.
                      </Text>
                    </div>
                  </Group>
                </Paper>

                <Divider label="or" labelPosition="center" />
              </>
            )}

            <Paper
              withBorder
              p="md"
              radius="md"
              style={{ cursor: "pointer" }}
              onClick={() => setSetupStep(2)}
            >
              <Group wrap="nowrap">
                <ThemeIcon size={44} radius="md" variant="light" color="gray">
                  <IconLock size={22} />
                </ThemeIcon>
                <div>
                  <Text fw={600} size="sm">
                    Master Password
                  </Text>
                  <Text size="xs" c="dimmed">
                    Choose a password. You'll enter it once each time you open
                    Signed+.
                  </Text>
                </div>
              </Group>
            </Paper>

            {busy && (
              <Center>
                <Group gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Setting up biometric...
                  </Text>
                </Group>
              </Center>
            )}

            <Button variant="subtle" size="xs" onClick={() => setSetupStep(0)}>
              ← Back
            </Button>
          </Stack>
        )}

        {/* ── PASSWORD FORM (setup) ─────────────────────────────── */}
        {isSetup && setupStep === 2 && (
          <Stack gap="md">
            <PasswordInput
              label="Master Password"
              placeholder="Choose a strong password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              size="md"
              autoFocus
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              size="md"
              onKeyDown={(e) => e.key === "Enter" && handleSetupPassword()}
            />
            <Text size="xs" c="dimmed">
              This password encrypts your key store locally. Signed+ cannot
              recover it if forgotten.
            </Text>
            <Group>
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setSetupStep(1);
                  setError(null);
                }}
              >
                ← Back
              </Button>
              <Button flex={1} onClick={handleSetupPassword} loading={busy}>
                Create Vault
              </Button>
            </Group>
          </Stack>
        )}

        {/* ── UNLOCK: WEBAUTHN ────────────────────────────────── */}
        {!isSetup && authMethod === "webauthn" && (
          <Stack gap="md">
            <Button
              size="lg"
              fullWidth
              leftSection={<IconFingerprint size={20} />}
              onClick={handleUnlockWebAuthn}
              loading={busy}
            >
              Unlock with Biometric
            </Button>
          </Stack>
        )}

        {/* ── UNLOCK: PASSWORD ────────────────────────────────── */}
        {!isSetup && authMethod === "password" && (
          <Stack gap="md">
            <PasswordInput
              label="Master Password"
              placeholder="Enter your vault password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              size="md"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleUnlockPassword()}
            />
            <Button
              fullWidth
              leftSection={<IconLock size={20} />}
              onClick={handleUnlockPassword}
              loading={busy}
            >
              Unlock
            </Button>
          </Stack>
        )}

        {/* Footer */}
        <Text size="xs" c="dimmed" ta="center">
          🔐 AES-256 encrypted · keys never leave your device
          {!isSetup && " · auto-locks after 30min"}
        </Text>
      </Stack>
    </Modal>
  );
}
